import { McpAccess, McpApp, MCP_TOOL_GROUPS, toMcpScope, type McpScope } from "./scopes";

/** The assistants the MCP settings walk someone through, each with its own place to paste. */
export enum McpClient {
  CLAUDE_CODE = "claude-code",
  CLAUDE_DESKTOP = "claude-desktop",
  CURSOR = "cursor",
  VS_CODE = "vs-code",
  OTHER = "other",
}

export type McpClientSetup = {
  /** `shell` is a command to run; `json` is a config file to merge in. */
  kind: "shell" | "json";
  /** Where it goes: the file to edit, or null for a terminal command. */
  file: string | null;
  snippet: string;
};

/** The server's name in the assistant's list: one per app, so all three can sit side by side. */
export const mcpServerName = (app: McpApp) => `complete-athlete-${app}`;

const json = (value: unknown) => JSON.stringify(value, null, 2);

/**
 * Exactly what to paste into each assistant, with the key already in it. Claude Desktop only
 * speaks to local servers from its config file, so it goes through `mcp-remote`, which forwards
 * to our URL and adds the header.
 */
export const mcpClientSetup = ({
  client,
  app,
  serverUrl,
  secret,
}: {
  client: McpClient;
  app: McpApp;
  serverUrl: string;
  secret: string;
}): McpClientSetup => {
  const name = mcpServerName(app);
  const authorization = `Bearer ${secret}`;

  switch (client) {
    case McpClient.CLAUDE_CODE:
      return {
        kind: "shell",
        file: null,
        snippet: `claude mcp add --transport http ${name} ${serverUrl} --header "Authorization: ${authorization}"`,
      };
    case McpClient.CLAUDE_DESKTOP:
      return {
        kind: "json",
        file: "claude_desktop_config.json",
        snippet: json({
          mcpServers: {
            [name]: {
              command: "npx",
              args: ["-y", "mcp-remote", serverUrl, "--header", "Authorization:${MCP_AUTHORIZATION}"],
              env: { MCP_AUTHORIZATION: authorization },
            },
          },
        }),
      };
    case McpClient.CURSOR:
      return {
        kind: "json",
        file: "~/.cursor/mcp.json",
        snippet: json({ mcpServers: { [name]: { url: serverUrl, headers: { Authorization: authorization } } } }),
      };
    case McpClient.VS_CODE:
      return {
        kind: "json",
        file: ".vscode/mcp.json",
        snippet: json({ servers: { [name]: { type: "http", url: serverUrl, headers: { Authorization: authorization } } } }),
      };
    case McpClient.OTHER:
      return {
        kind: "json",
        file: null,
        snippet: json({ transport: "streamable-http", url: serverUrl, headers: { Authorization: authorization } }),
      };
  }
};

/** How far a key reaches into one group in the picker: not at all, reading, or reading and changing. */
export type McpGroupAccess = "none" | McpAccess;

export const mcpScopesFromAccess = (access: Record<string, McpGroupAccess>): McpScope[] =>
  Object.entries(access).flatMap(([group, level]) => (level === "none" ? [] : [toMcpScope(group, level)]));

export const mcpAccessFromScopes = (app: McpApp, scopes: readonly string[]): Record<string, McpGroupAccess> =>
  Object.fromEntries(
    MCP_TOOL_GROUPS[app].map((group) => [
      group,
      scopes.includes(toMcpScope(group, McpAccess.WRITE))
        ? McpAccess.WRITE
        : scopes.includes(toMcpScope(group, McpAccess.READ))
          ? McpAccess.READ
          : "none",
    ]),
  );

/** The two one-click choices: everything, or everything read-only. */
export const mcpPresetAccess = (app: McpApp, level: McpAccess): Record<string, McpGroupAccess> =>
  Object.fromEntries(MCP_TOOL_GROUPS[app].map((group) => [group, level]));

/** Which preset a set of scopes is, if any, so a key's row can say "Full access" instead of a list. */
export const mcpPresetOf = (app: McpApp, scopes: readonly string[]): McpAccess | null => {
  const access = mcpAccessFromScopes(app, scopes);
  const levels = new Set(Object.values(access));
  if (levels.size !== 1) return null;
  const [level] = levels;
  return level === "none" ? null : level;
};
